//#region Imports

// angular imports
import { Component, Injector, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';

// 3rd party imports
import { Column } from 'devextreme/ui/data_grid';
import { Subscription } from 'rxjs';

// cc-common imports
import { MessageBoxComponent, Utils } from 'cc-common';

// component imports
import { BoardApprovalBaseComponent } from '../../_base/board-approval-base.component';
import { BoardApprovalGridComponent } from '../board-approval-grid/board-approval-grid.component';

// service imports
import { CommunityService } from '../../../_services/data-services/community.service';
import { ContextManagerService } from '../../../_services/data-services/context-manager.service';

// model imports
import { CommunitySelectorResultDto } from '../../../_models/community-selector-result-dto';
import { BoardApprovalActionsResultDto } from '../../../shared-acc-board-approval-module/_models/board-approval-actions-result-dto';
import { BoardApprovalItemResultDto } from '../../../shared-acc-board-approval-module/_models/board-approval-item-result-dto';
import { BoardApprovalParameterDto } from '../../../shared-acc-board-approval-module/_models/board-approval-parameter-dto';
import { BoardApprovalsActionParametersDto } from '../../../shared-acc-board-approval-module/_models/board-approvals-action-parameters-dto';
import { BoardApprovalAllowedActionsParameterDto } from '../../_models/board-approval-allowed-actions-parameter-dto';
import { BoardApprovalAllowedActionsResultDto } from '../../_models/board-approval-allowed-actions-result-dto';
import { BoardApprovalQueueParameterDto } from '../../../shared-acc-board-approval-module/_models/board-approval-queue-parameter-dto';

// constant imports
import { Constants } from '../../../_constants/constants';
import { BoardApprovalProcessConstants } from '../../../shared-acc-board-approval-module/_constants/board-approval-process-constants';
import { BoardApprovalActionConstants } from '../../../shared-acc-board-approval-module/_constants/board-approvals-action-constants';

// Component imports
import { MultiColumnSelectorComponent } from '../../../management-common/component/common/multi-column-selector/multi-column-selector.component';
import { ICommunityContextDashboardComponent } from '../../../dashboard-module/_interfaces/community-context-component';
import { IInteractiveDashboardComponent } from '../../../dashboard-module/_interfaces/interactive-dashboard-component';

//#endregion

@Component({
    selector: 'app-board-approval',
    templateUrl: './board-approval.component.html',
    styleUrls: ['./board-approval.component.scss']
})
export class BoardApprovalComponent
    extends BoardApprovalBaseComponent
    implements OnInit, OnDestroy, ICommunityContextDashboardComponent, IInteractiveDashboardComponent
{
    //#region Component variables

    @ViewChild('boardApprovalGrid', { static: false }) boardApprovalGrid: BoardApprovalGridComponent;
    @ViewChild('boardApprovalMessageBox', { static: false }) boardApprovalMessageBox: MessageBoxComponent;
    @ViewChild('communitySelector', { static: false }) communitySelector: MultiColumnSelectorComponent;

    hideTitle = false;
    minWidthRatio = 30;
    minHeightRatio = 30;
    minWidth = 1100;
    minHeight = 600;
    hideFullScreen = false;
    widgetId: string;

    helpLink: string;
    pageTitle: string;

    loadingMessage: string;
    loadingVisible: boolean;

    tabItems: Array<any> = [];
    selectedTabItem: string;

    multipleActions: Array<BoardApprovalActionsResultDto> = [];
    isMultiSelectionDisabled = true;
    isActionsLoaded = false;

    communityList: CommunitySelectorResultDto[] = [];
    communityColumns: Array<Column>;
    communityGridBoxValue: Array<number> = [];
    selectedCommunityId: number;

    taskTypes: Array<any> = [];
    actionTitle: string;
    selectedApprovalRecords: Array<BoardApprovalItemResultDto> = [];

    showMultipleBoardApproval = false;
    multipleBoardApprovalAction: BoardApprovalActionsResultDto;

    boardApprovalRefreshSubscription: Subscription;
    contextManagerSubscription: Subscription;
    showCommunitySelector = false;

    formLabelLocation = Constants.defaultFormLabelLocation;

    filtersColCount = 5;
    filterColCountDivider = 260;

    //#endregion

    //#region Component life cycle methods

    constructor(
        protected readonly injector: Injector,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly communityService: CommunityService,
        private readonly contextManagerService: ContextManagerService
    ) {
        super(injector);

        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                const routeData = this.route.snapshot.data;
                if (Utils.notNullAndDefined(routeData)) {
                    this.pageTitle = routeData.pageTitle;
                    this.helpLink = this.ciraHelpService.getHelpLink(null, routeData.helpKey, null);
                }
            }
        });

        this.boardApprovalHelperService.onLoadingEvent().subscribe((loader: any) => {
            setTimeout(() => {
                this.loadingMessage = loader.message;
                this.loadingVisible = loader.showLoading;
            }, 10);
        });
    }

    ngOnInit(): void {
        this.getCommunityColumns();
        this.getCommunityList();

        this.configureBoardApprovals();
        this.updateSelectedContext();

        this.contextManagerSubscription = this.contextManagerService.getContext().subscribe(() => {
            this.updateSelectedContext();
        });

        // Event subscription for multi records action
        this.boardApprovalRefreshSubscription = this.boardApprovalService
            .refreshBoardApprovalRecords()
            .subscribe(() => {
                this.closeAllPopups();

                // After multi records action successful, Reset the queueParams communityId for Portfolio context if no community is selected
                const selectedContext = this.contextManagerService.getSelectedContext();
                if (Utils.isNullOrUndefined(selectedContext) && Utils.isNullOrUndefined(this.selectedCommunityId)) {
                    this.queueParams.communityId = null;
                }

                this.boardApprovalGrid.resetGridSelection();
                this.boardApprovalGrid.reloadBoardApprovalQueue();
            });

        this.route.queryParams.subscribe((params: Params) => {
            if (Utils.notNullAndDefined(params['communityId'])) {
                this.queueParams = new BoardApprovalQueueParameterDto();
                this.queueParams.communityId = Number(params['communityId']);
                this.communityGridBoxValue = [this.queueParams.communityId];
                this.queueParams.itemId = Number(params['accItemId']);
                this.queueParams.isClosedQueue = false;
                this.queueParams.myTask = false;
            }
        });
    }

    ngOnDestroy(): void {
        if (Utils.notNullAndDefined(this.contextManagerSubscription)) {
            this.contextManagerSubscription.unsubscribe();
        }
        if (Utils.notNullAndDefined(this.boardApprovalRefreshSubscription)) {
            this.boardApprovalRefreshSubscription.unsubscribe();
        }
    }

    //#endregion

    //#region Overriden methods

    setCommunity(communityId: number, managementCompanyId: number, communityName: string): void {
        // Set community selector
        this.communityGridBoxValue = [communityId];
    }

    refresh(width: number, height: number): void {
        this.filtersColCount =
            Math.round(width / this.filterColCountDivider) > 6 ? 6 : Math.round(width / this.filterColCountDivider);
    }

    getTitle(name: string): string {
        return name;
    }

    //#endregion

    //#region Event Handlers

    onTabItemClick(event: any): void {
        this.tabItems.forEach((code) => {
            if (Utils.equalsIgnoreCase(code.name, event.itemData.name)) {
                code.selected = true;
                this.selectedTabItem = code;
                this.queueParams.isClosedQueue = code.value;
            } else {
                code.selected = false;
            }
        });
        this.boardApprovalGrid.reloadBoardApprovalQueue();
    }

    onCommunitySelection(selectedRow: CommunitySelectorResultDto[]): void {
        if (Utils.notNullOrEmpty(selectedRow)) {
            this.queueParams.communityId = selectedRow[0].communityId;
            this.selectedCommunityId = selectedRow[0].communityId;
        } else {
            this.queueParams.communityId = null;
            this.communityGridBoxValue = [];
            this.selectedCommunityId = null;

            this.communitySelector.resetSelector();
        }
        this.onRefreshClick();
    }

    onActionItemClick(event: any): void {
        const itemData: BoardApprovalActionsResultDto = event.itemData;

        if (Utils.isTrue(this.isInternalUser)) {
            const dialog = this.helperService.getConfirmationDialog(
                'Confirmation',
                `This action will set the status to ${itemData.displayText} on <br />behalf of all approvers. Are you sure you want to continue?`,
                'Yes',
                'No',
                'success'
            );

            dialog.show().then((dialogResult) => {
                if (Utils.isTrue(dialogResult)) {
                    this.applyAction(itemData);
                }
            });
        } else {
            this.applyAction(itemData);
        }
    }

    onApprovalRowSelection(records: Array<BoardApprovalItemResultDto>): void {
        if (Utils.notNullOrEmpty(records)) {
            this.selectedApprovalRecords = records;
            this.isMultiSelectionDisabled = false;
        } else {
            this.isMultiSelectionDisabled = true;
            this.isActionsLoaded = false;
            this.multipleActions = [];
            this.selectedProcess = null;
            this.recordsToProcess = [];
        }

        if (Utils.isTrue(this.validateSelectedBoardApprovalRecords())) {
            this.buildBoardApprovalRecordsToProcess();

            // When first board approval record is selected, load the board approval actions of community for process
            if (
                Utils.isFalse(this.isActionsLoaded) &&
                Utils.isFalse(this.isMultiSelectionDisabled) &&
                Utils.notNullOrEmpty(records)
            ) {
                this.populateBoardapprovalActions(records[0].communityId, records[0].process, records[0].status);
            }
        }
    }

    onMultipleBoardApprovalEditorClose(): void {
        this.showMultipleBoardApproval = false;
    }

    onRefreshClick(): void {
        this.boardApprovalGrid.reloadBoardApprovalQueue();
    }

    //#endregion

    //#region Private methods

    private configureBoardApprovals(): void {
        this.queueParams = new BoardApprovalQueueParameterDto();
        this.tabItems = [
            {
                text: 'Open',
                name: 'OPEN',
                value: false,
                selected: true
            },
            {
                text: 'Closed',
                name: 'CLOSED',
                value: true,
                selected: false
            }
        ];

        this.selectedTabItem = this.tabItems[0];
        this.queueParams.isClosedQueue = false;

        this.taskTypes = [
            {
                displayText: 'My Tasks',
                value: true
            },
            {
                displayText: 'All Tasks',
                value: false
            }
        ];
    }

    private applyAction(itemData: BoardApprovalActionsResultDto): void {
        if (Utils.notNullOrEmpty(this.selectedApprovalRecords)) {
            const communityId = this.selectedApprovalRecords[0].communityId;
            const selectedProcess = this.selectedApprovalRecords[0];

            this.multipleBoardApprovalAction = itemData;
            this.actionTitle = `${selectedProcess.processDisplayText} - ${itemData.displayText}`;
            this.selectedProcess = selectedProcess.process;

            if (Utils.isTrue(itemData.reasonRequiredForAction)) {
                // Update the community id of param dto
                this.queueParams.communityId = communityId;

                // Show mutiple board approval editor
                this.showMultipleBoardApproval = true;
            } else {
                const params: BoardApprovalsActionParametersDto = new BoardApprovalsActionParametersDto();
                params.isClosed = this.queueParams.isClosedQueue;
                params.communityId = communityId;
                params.boardApprovalData = this.recordsToProcess;

                switch (this.selectedProcess) {
                    case BoardApprovalProcessConstants.ACC:
                        this.saveAccAprrovals(itemData.actionCode, params);
                        break;
                    case BoardApprovalProcessConstants.COLLECTIONS:
                        this.saveCollectionApprovals(itemData.actionCode, params);
                        break;
                    case BoardApprovalProcessConstants.WF:
                        this.saveWFApprovals(itemData.actionCode, params);
                        break;
                }
            }
        }
    }

    private updateSelectedContext(): void {
        const selectedContext = this.contextManagerService.getSelectedContext();
        if (Utils.notNullAndDefined(selectedContext)) {
            this.queueParams.communityId = selectedContext.communityId;
            this.queueParams.myTask = false;
            this.showCommunitySelector = false;
        } else {
            this.queueParams.communityId = null;
            this.queueParams.myTask = true;
            this.showCommunitySelector = true;
        }
    }

    private closeAllPopups(): void {
        this.showMultipleBoardApproval = false;
    }

    private getCommunityList(): void {
        this.communityService.getCommunities().subscribe(
            (result: CommunitySelectorResultDto[]) => {
                this.communityList = result;
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private getCommunityColumns(): void {
        this.communityColumns = [
            {
                dataField: 'communityName',
                dataType: 'string',
                caption: 'Community',
                allowHeaderFiltering: false
            },
            {
                dataField: 'locationName',
                dataType: 'string',
                caption: 'Branch'
            },
            {
                dataField: 'managementCompanyName',
                dataType: 'string',
                caption: 'Management Company'
            }
        ];
    }

    /**
     * Method to validate selected board approval records are of same community, process and status
     */
    private validateSelectedBoardApprovalRecords(): boolean {
        const distinctProcesses: Array<string> = this.selectedApprovalRecords
            .map((record) => record.process)
            .filter((vlaue, index, array) => Utils.equals(array.indexOf(vlaue), index));

        const distinctStatuses: Array<string> = this.selectedApprovalRecords
            .map((record) => record.status)
            .filter((vlaue, index, array) => Utils.equals(array.indexOf(vlaue), index));

        const distinctCommunityIds: Array<number> = this.selectedApprovalRecords
            .map((record) => record.communityId)
            .filter((vlaue, index, array) => Utils.equals(array.indexOf(vlaue), index));

        if (
            Utils.isNullOrEmpty(distinctProcesses) ||
            distinctProcesses.length > 1 ||
            Utils.isNullOrEmpty(distinctStatuses) ||
            distinctStatuses.length > 1 ||
            Utils.isNullOrEmpty(distinctCommunityIds) ||
            distinctCommunityIds.length > 1
        ) {
            this.boardApprovalMessageBox.showErrorMessage(
                'Error',
                'Selected processes should be of same community and process with same status.'
            );

            return false;
        }

        return true;
    }

    private populateBoardapprovalActions(communityId: number, processName: string, status: string): void {
        this.multipleActions = [];

        // Build param dto
        const param = new BoardApprovalAllowedActionsParameterDto();
        param.communityId = communityId;
        param.processName = processName;

        // Show loading
        this.boardApprovalHelperService.showLoading();

        // Make API call
        this.boardApprovalService.getActions(param).subscribe(
            (result: BoardApprovalAllowedActionsResultDto) => {
                // Set the icon class for each action item
                result.actions.forEach((action: BoardApprovalActionsResultDto) => {
                    switch (action.imageIndicator) {
                        case BoardApprovalActionConstants.GREEN_CHECK:
                            action.icon = `${this.iconCheck} cc-icon-check`;
                            break;
                        case BoardApprovalActionConstants.RED_CROSS:
                            action.icon = `${this.iconCross} cc-icon-cross`;
                            break;
                        case BoardApprovalActionConstants.WARNING:
                            action.icon = `${this.iconWarn} cc-warn-icon`;
                            break;
                        default:
                            action.icon = null;
                            break;
                    }
                });

                if (Utils.isTrue(result.allowApprovalWithModification)) {
                    this.multipleActions = result.actions;
                } else {
                    if (Utils.equalsIgnoreCase(status, 'APPROVED')) {
                        this.multipleActions = result.actions;
                    } else {
                        this.multipleActions = result.actions.filter((d) =>
                            Utils.notEqualsIgnoreCase(d.actionCode, 'ApprovedComment')
                        );
                    }
                }

                this.isActionsLoaded = true;

                // Hide loading
                this.boardApprovalHelperService.hideLoading();
            },
            (error) => {
                // Hide loading
                this.boardApprovalHelperService.hideLoading();

                // Display error
                this.errorService.displayError(error);
            }
        );
    }

    private buildBoardApprovalRecordsToProcess(): void {
        this.recordsToProcess = [];

        // Build the recordsToProcess list
        this.selectedApprovalRecords.forEach((record) => {
            // Find record to proces by process id
            const recordToProcess = this.recordsToProcess.find((d) => Utils.equals(d.processId, record.processId));

            if (Utils.isNullOrUndefined(recordToProcess)) {
                const process = new BoardApprovalParameterDto();

                process.processId = record.processId;
                process.notes = null;
                process.responseToOwner = null;
                process.description = record.description;
                process.processIdDisplayText = record.processIdDisplayText;
                process.userInfo = record.userInfo;

                // Add in list
                this.recordsToProcess.push(process);
            }
        });
    }

    //#endregion
}
